import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private messageSubject = new BehaviorSubject<string[]>([]);
  private errorSubject = new BehaviorSubject<string[]>([]);

  readonly message$ = this.messageSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();

  private messageTimeout: any = null;
  private errorTimeout: any = null;

  showMessage(messages: string | string[]): void {
    const currentMessages = this.messageSubject.getValue();
    const newMessages = currentMessages.concat(messages);

    this.messageSubject.next(newMessages);
    this.resetMessageTimer();
  }

  showError(error: any): void {
    const newErrors = this.extractServerErrors(error);
    const currentErrors = this.errorSubject.getValue();
    this.errorSubject.next(currentErrors.concat(newErrors));
    this.resetErrorTimer();
  }

  private resetMessageTimer(): void {
    clearTimeout(this.messageTimeout);
    this.messageTimeout = setTimeout(() => {
      this.messageSubject.next([]);
    }, 4000);
  }

  private resetErrorTimer(): void {
    clearTimeout(this.errorTimeout);
    this.errorTimeout = setTimeout(() => {
      this.errorSubject.next([]);
    }, 4000);
  }

  extractServerErrors(error: any): string[] {
    const response = error?.error ?? error;

    if (Array.isArray(response?.errors)) {
      return response.errors.map((serverError: any) => this.toMessage(serverError));
    }

    if (response?.errors && typeof response.errors === 'object') {
      return Object.values(response.errors)
        .flat()
        .map((serverError: any) => this.toMessage(serverError));
    }

    if (Array.isArray(response)) {
      return response.map((serverError: any) => this.toMessage(serverError));
    }

    if (typeof response === 'string') {
      return [response];
    }

    if (response?.message) {
      return [response.message];
    }

    if (error?.message) {
      return [error.message];
    }

    return ['An unknown error occurred.'];
  }

  private toMessage(serverError: any): string {
    if (typeof serverError === 'string') {
      return serverError;
    }

    return serverError?.message ??
      serverError?.description ??
      serverError?.code ??
      'An unknown error occurred.';
  }

  clearMessages(): void {
    this.messageSubject.next([]);
    clearTimeout(this.messageTimeout);
  }

  clearErrors(): void {
    this.errorSubject.next([]);
    clearTimeout(this.errorTimeout);
  }
}
